import Config, { ENV_DEVELOPMENT, ENV_STAGING } from "core/model/config";
import trimDecrypted from "core/model/encryption/trimDecrypted";
import { b64DecodeUnicode, b64EncodeUnicode } from "core/model/utils/base64";
import { isCI } from "core/model/utils/featureFlags";
import { Auction, GetOntologyType } from "core/types";
import { Locale as DateFnsLocale } from "date-fns";
import { padding } from "ds_legacy/materials/metrics";
import { StaticFaxCoverLetter } from "dsl/static/StaticFaxCoverLetter";
import { StaticPatientProfile } from "dsl/static/StaticPatientProfile";
import ReactDOMServer from "react-dom/server";
import { Locale as TranslationsLocale } from "translations";
import Translations from "translations/types";

export function getPatientProfileHTML({
  auction,
  currentLocale,
  getOntology,
  isSealdOnly,
  locale,
  translations,
  type,
  withEncryptionAccess,
}: {
  auction: Auction;
  currentLocale: DateFnsLocale;
  faxPrint?: boolean;
  forClinic?: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  translations: Translations;
  type: number;
  isSealdOnly: boolean;
  withEncryptionAccess: boolean;
}) {
  const safeAuction = trimDecrypted(auction);

  /**
   * Caution: Do not use hooks within renderToStaticMarkup!
   *
   * Reason: React hooks rely on the component lifecycle, and calling hooks within
   * renderToStaticMarkup will break the rules of hooks. This is because renderToStaticMarkup
   * runs outside of React's regular rendering lifecycle, meaning hooks will not behave as expected.
   * Using hooks here can lead to unexpected behavior and errors. It's important to only call hooks
   * inside functional components or custom hooks, where the component lifecycle is guaranteed.
   */
  const patientProfileHTML = ReactDOMServer.renderToStaticMarkup(
    <>
      <div>
        <StaticFaxCoverLetter
          auction={safeAuction}
          faxType={type}
          getOntology={getOntology}
          translations={translations}
        />
      </div>
      <div style={{ padding: padding(0) }}>
        <StaticPatientProfile
          auction={safeAuction}
          currentLocale={currentLocale}
          getOntology={getOntology}
          isFaxPrint
          isSealdOnly={isSealdOnly}
          locale={locale}
          translations={translations}
          withEncryptionAccess={withEncryptionAccess}
        />
      </div>
    </>,
  );

  const content =
    `<!DOCTYPE html>` +
    `<html>` +
    `<head><meta charset="UTF-8" /></head>` +
    patientProfileHTML +
    `</html>`;

  const base64encodedContent = b64EncodeUnicode(content);

  const isDevOrStaging =
    (Config.environment === ENV_DEVELOPMENT ||
      Config.environment === ENV_STAGING) &&
    !isCI &&
    // @ts-ignore
    !window.__STORYBOOK_CLIENT_API__;

  if (!isDevOrStaging) return base64encodedContent;

  const newWindow = window.open("about:blank", "", "_blank");
  if (!newWindow) {
    console.log("Could not open new window to show fax document");
  } else {
    newWindow.document.write(b64DecodeUnicode(base64encodedContent));
  }

  return base64encodedContent;
}
